import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { BoardApi } from "../../../services/board-service-api";
import { StreamingHelperService } from "../../../services/streaming-helper-service";
import { BoardNotification } from "./types";

// Backend-backed notification store. The server owns notification generation and
// recipient resolution (a stakeholder is notified about others' actions, never
// their own), so the frontend just fetches its own feed and tracks read-state via
// the backend — no activity-log derivation, no localStorage read marker.

interface NotificationsListener {
  (): void;
}

let cache: BoardNotification[] = [];
const listeners: Set<NotificationsListener> = new Set();

// The real-time stream prepends notifications for the whole tab session (the
// left-menu badge keeps it alive across designer + management), so cap the
// in-memory feed to bound growth over a multi-day session. refreshNotifications()
// still replaces this with the server's authoritative list.
const MAX_CACHED_NOTIFICATIONS = 200;

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeNotifications(listener: NotificationsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getNotifications(): BoardNotification[] {
  return cache;
}

export function unreadNotificationCount(): number {
  return cache.filter((notification: BoardNotification) => !notification.read).length;
}

export async function refreshNotifications(): Promise<void> {
  try {
    cache = await BoardApi.getNotifications();
    emit();
  } catch {
    // exceptionHandler already surfaced the error.
  }
}

export async function markNotificationsRead(): Promise<void> {
  if (cache.every((notification: BoardNotification) => notification.read)) return;
  try {
    await BoardApi.markNotificationsRead();
    cache = cache.map((notification: BoardNotification) => ({ ...notification, read: true }));
    emit();
  } catch {
    // exceptionHandler already surfaced the error.
  }
}

// Real-time push: a single Server-Sent Events stream delivers notifications the
// backend raises for this user. We use the fetch-based StreamingHelperService (NOT
// the native EventSource, which can't send the designer's Bearer auth header) and
// reconnect on drop. Idempotent — safe to call from every consumer's mount; there
// is no polling.
const RECONNECT_DELAY_MS: number = 3000;
let streaming: boolean = false;
let streamAbort: AbortController | null = null;

function onStreamEvent(data: unknown): void {
  const incoming: BoardNotification = data as BoardNotification;
  if (incoming && incoming.id && !cache.some((notification: BoardNotification) => notification.id === incoming.id)) {
    cache = [incoming, ...cache].slice(0, MAX_CACHED_NOTIFICATIONS);
    emit();
  }
}

async function runNotificationStream(): Promise<void> {
  const url: string = `${CommonHelperFunctions.GetBackendUrl()}/board/notifications/stream`;
  while (streaming) {
    const controller: AbortController = new AbortController();
    streamAbort = controller;
    try {
      await StreamingHelperService.streamEvents(url, onStreamEvent, controller.signal);
    } catch {
      // network/stream error — fall through to the reconnect delay
    }
    if (!streaming) break;
    await new Promise<void>((resolve: () => void) => setTimeout(resolve, RECONNECT_DELAY_MS));
  }
}

export function connectNotificationStream(): void {
  if (streaming) return;
  if (typeof fetch === "undefined") return; // SSR / unsupported environment
  streaming = true;
  void runNotificationStream();
}

export function disconnectNotificationStream(): void {
  streaming = false;
  if (streamAbort) {
    streamAbort.abort();
    streamAbort = null;
  }
}
